import React, { useState, useEffect } from 'react';
import { getAllTours, updateTourStatus } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

// MUI Components
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';

// MUI Icons
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DateRangeIcon from '@mui/icons-material/DateRange';
import HomeIcon from '@mui/icons-material/Home';

// Tour status chip styles
const getStatusChipStyle = (status: string) => {
  switch (status) {
    case 'SCHEDULED':
      return { bg: '#E1FCEF', color: '#10B981' };
    case 'PENDING':
      return { bg: '#FFF4E5', color: '#FF8A00' };
    case 'COMPLETED':
      return { bg: '#F0EDFF', color: '#7065F0' };
    case 'CANCELLED':
      return { bg: '#FEEEF0', color: '#F43F5E' };
    case 'RESCHEDULED':
      return { bg: '#F0F9FF', color: '#0EA5E9' };
    default:
      return { bg: '#F3F4F6', color: '#6B7280' };
  }
};

// Format date and time
const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (dateString: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const ToursManagement: React.FC = () => {
  const { admin } = useAuth();
  const [tours, setTours] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalTours, setTotalTours] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDate, setFilterDate] = useState<string>('');
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [selectedTour, setSelectedTour] = useState<any>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');

  // Fetch tours
  const fetchTours = async () => {
    try {
      setLoading(true);
      const response = await getAllTours({
        status: filterStatus,
        startDate: filterStartDate,
        endDate: filterEndDate,
        page: page + 1,
        limit: rowsPerPage,
      });
      
      setTours(response.tours);
      setTotalTours(response.pagination.totalTours);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch tours');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchTours();
  }, [page, rowsPerPage, filterStatus, filterStartDate, filterEndDate]);

  // Handle search
  const handleSearch = () => {
    // In a real app, this would filter by property name through an API call
    // For now, we'll reset to page 1 and fetch tours
    setPage(0);
    fetchTours();
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open edit dialog
  const handleOpenEditDialog = (tour: any) => {
    setSelectedTour(tour);
    setNewStatus(tour.status);
    setEditDialogOpen(true);
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedTour(null);
    setNewStatus('');
  };

  // Open view dialog
  const handleOpenViewDialog = (tour: any) => {
    setSelectedTour(tour);
    setViewDialogOpen(true);
  };

  // Close view dialog
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedTour(null);
  };

  // Update tour status
  const handleUpdateStatus = async () => {
    if (!selectedTour || !newStatus) return;

    try {
      await updateTourStatus(selectedTour._id, { status: newStatus });
      toast.success('Tour status updated successfully');
      
      // Update local state to reflect the change
      setTours(tours.map(t => 
        t._id === selectedTour._id 
          ? { ...t, status: newStatus } 
          : t
      ));
      
      handleCloseEditDialog();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update tour status');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, color: '#0F0F2D', mb: 4 }}>
        Tours Management
      </Typography>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              placeholder="Search property name..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                <MenuItem value="RESCHEDULED">Rescheduled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="End Date"
              type="date"
              size="small"
              fullWidth
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Button 
              variant="contained" 
              fullWidth
              onClick={handleSearch}
              sx={{
                backgroundColor: '#7065F0',
                '&:hover': {
                  backgroundColor: '#5A52C4',
                },
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress size={60} sx={{ color: '#7065F0' }} />
        </Box>
      )}

      {/* Empty State */}
      {!loading && tours.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <EventIcon sx={{ fontSize: 60, color: '#9CA3AF', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
            No tours found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Paper>
      )}

      {/* Tours Table */}
      {!loading && tours.length > 0 && (
        <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#F7F7FD' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tours.map((tour) => {
                const statusStyle = getStatusChipStyle(tour.status);
                
                return (
                  <TableRow key={tour._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <HomeIcon fontSize="small" sx={{ color: '#6B7280', mr: 1 }} />
                        <Typography variant="body2">
                          {tour.property?.propertyName || tour.property?.propertyType || 'Unknown Property'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon fontSize="small" sx={{ color: '#6B7280', mr: 1 }} />
                        <Typography variant="body2">
                          {tour.user?.name || 'Unknown User'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DateRangeIcon fontSize="small" sx={{ color: '#6B7280', mr: 1 }} />
                        <Typography variant="body2">
                          {formatDate(tour.tourDate)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={tour.status} 
                        size="small"
                        sx={{ 
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenViewDialog(tour)}
                            sx={{ color: '#6B7280' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Status">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenEditDialog(tour)}
                            sx={{ color: '#7065F0' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalTours}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Edit Tour Status Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Edit Tour Status
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Update the status for the tour of "{selectedTour?.property?.propertyName || selectedTour?.property?.propertyType || 'Unknown Property'}" by {selectedTour?.user?.name || 'Unknown User'}
          </DialogContentText>
          
          <FormControl fullWidth>
            <InputLabel id="edit-status-label">Status</InputLabel>
            <Select
              labelId="edit-status-label"
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
              <MenuItem value="RESCHEDULED">Rescheduled</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            onClick={handleUpdateStatus} 
            variant="contained"
            sx={{
              backgroundColor: '#7065F0',
              '&:hover': {
                backgroundColor: '#5A52C4',
              },
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Tour Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Tour Details
        </DialogTitle>
        <DialogContent>
          {selectedTour && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Property Information
                    </Typography>
                    
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {selectedTour.property?.propertyName || selectedTour.property?.propertyType || 'Unknown Property'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOnIcon fontSize="small" sx={{ color: '#9CA3AF', mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {selectedTour.property?.flatHouseNo ? `${selectedTour.property.flatHouseNo}, ` : ''}
                        {selectedTour.property?.address || ''} 
                        {selectedTour.property?.city ? `, ${selectedTour.property.city}` : ''}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedTour.property?.propertyType || ''} {selectedTour.property?.bhk ? `• ${selectedTour.property.bhk} BHK` : ''}
                    </Typography>
                    
                    <Typography variant="body1" fontWeight={600} color="#0F0F2D">
                      ₹{selectedTour.property?.monthlyRent ? selectedTour.property?.seo?.price : '0'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /month
                      </Typography>
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      User Information
                    </Typography>
                    
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {selectedTour.user?.name || 'Unknown User'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedTour.user?.email || 'N/A'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedTour.user?.phone || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Tour Details
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Tour Date & Time
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <DateRangeIcon fontSize="small" sx={{ color: '#6B7280', mr: 0.5 }} />
                          <Typography variant="body1">
                            {formatDateTime(selectedTour.tourDate)}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Tour Status
                        </Typography>
                        <Chip 
                          label={selectedTour.status} 
                          size="small"
                          sx={{ 
                            mt: 0.5,
                            backgroundColor: getStatusChipStyle(selectedTour.status).bg,
                            color: getStatusChipStyle(selectedTour.status).color,
                            fontWeight: 500,
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Tour Mode
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {selectedTour.tourMode || 'In-person'}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Additional Notes
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {selectedTour.notes || 'No additional notes provided.'}
                      </Typography>
                    </Box>
                    
                    {/* Payment section for rent - not "rental" */}
                    {selectedTour.status === 'COMPLETED' && selectedTour.rentPayment && (
                      <Box sx={{ mt: 3, p: 2, bgcolor: '#F9FAFB', borderRadius: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          Rent Payment Information
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Payment Status
                            </Typography>
                            <Chip 
                              label={selectedTour.rentPayment.status} 
                              size="small"
                              sx={{ 
                                mt: 0.5,
                                backgroundColor: selectedTour.rentPayment.status === 'PAID' ? '#E1FCEF' : '#FFF4E5',
                                color: selectedTour.rentPayment.status === 'PAID' ? '#10B981' : '#FF8A00',
                                fontWeight: 500,
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Amount
                            </Typography>
                            <Typography variant="body1">
                              ₹{selectedTour.rentPayment.amount?.toLocaleString('en-IN') || '0'}
                            </Typography>
                          </Grid>
                          
                          {selectedTour.rentPayment.paidAt && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Payment Date
                              </Typography>
                              <Typography variant="body1">
                                {formatDate(selectedTour.rentPayment.paidAt)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          <Button 
            onClick={() => {
              handleCloseViewDialog();
              handleOpenEditDialog(selectedTour);
            }} 
            variant="contained"
            sx={{
              backgroundColor: '#7065F0',
              '&:hover': {
                backgroundColor: '#5A52C4',
              },
            }}
          >
            Edit Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ToursManagement; 